import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
} from "@/components/DecisionPageLayout";
import {
  SpravatoSafetyBlock,
  SpravatoInternalLinks,
} from "@/components/SpravatoSafetyBlock";
const SpravatoTriedEverything = () => (
  <DecisionPageLayout
    seo={{
      title:
        "“I've Tried Everything” — SPRAVATO® for Treatment-Resistant Depression | Neuro Wellness TMS Centers of America",
      description:
        "When the list of antidepressants you've tried gets long enough that you stop counting. Learn about SPRAVATO® — a different mechanism for treatment-resistant depression.",
      canonical: "/spravato-tried-everything",
      keywords:
        "treatment-resistant depression, SPRAVATO, tried everything, antidepressants not working, Coral Springs FL",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "I've Tried Everything — SPRAVATO® for Treatment-Resistant Depression",
        about:
          "SPRAVATO® (esketamine) for treatment-resistant depression when multiple antidepressants haven't worked.",
        provider: {
          "@id": "https://neurowellnesstms.com/#organization",
        },
      },
    }}
    badge="SPRAVATO® · Recognition"
    title="When the list gets long enough that you stop counting"
    subtitle="You could probably name six or seven of them. Maybe more."
    ctaHeading="Bring your list to the conversation"
    ctaText="If you can, put together a rough list — which medications, roughly when, what happened. It matters both clinically and for insurance. Schedule a free phone consultation or call (954) 827-9793."
  >
    <DecisionSection heading="You could probably name six or seven of them. Maybe more.">
      <p>
        The one that made you gain thirty pounds. The one that flattened
        everything, good and bad. The one that worked for four months and then
        quietly stopped. The one your doctor was excited about.
      </p>
      <p>
        Somewhere in there you started keeping a mental list, and somewhere
        after that you stopped bothering.
      </p>
      <p>
        The appointments have a rhythm now. Your prescriber asks how you're
        doing. You say something honest but not too honest. They suggest
        adjusting the dose, or adding something, or trying a different class.
        You agree, because what else is there. You go home with a new
        prescription and a familiar feeling — not hope exactly, more like the
        memory of hope.
      </p>
    </DecisionSection>

    <DecisionSection heading="What this actually means">
      <p>
        About a third of people with depression don't get adequate relief from
        oral antidepressants. That's not a small unlucky minority. It's a known
        limitation of how those medications work.
      </p>
      <p>
        If you've tried two or more without adequate response, there's a
        clinical term for where you are: treatment-resistant depression. It
        sounds like a judgment. It isn't. It's a category that exists
        specifically because this is common enough to need one — and because
        treatments were developed for it.
      </p>
      <p>
        You are not failing at treatment. The treatment has been failing you,
        and there are other mechanisms.
      </p>
    </DecisionSection>

    <DecisionSection heading="Why SPRAVATO® is a different conversation">
      <p>
        SPRAVATO® is esketamine. It's FDA-approved for treatment-resistant
        depression, and it acts on a different system in the brain than the
        medications you've likely been cycling through. That's the whole reason
        it exists — for people the standard mechanism hasn't reached.
      </p>
      <p>
        It's not a pill. You come to a certified center, self-administer a nasal
        spray under supervision, and stay for at least two hours of monitoring.
        It is a real commitment, and it has real risks, including a boxed
        warning you should read before deciding anything.
      </p>
      <p>
        But it is genuinely a different approach, not another version of what
        you've already tried.
      </p>
    </DecisionSection>

    <DecisionSection heading="What to bring to the conversation">
      <p>
        If you can, put together a rough list — which medications, roughly when,
        what happened. Approximate is fine. It matters both clinically and for
        insurance, since prior authorization usually requires documentation of
        prior trials.
      </p>
    </DecisionSection>

    <SpravatoInternalLinks />
    <SpravatoSafetyBlock />
  </DecisionPageLayout>
);
export default SpravatoTriedEverything;
